import React, { useMemo } from 'react';
import { generatePath, Redirect, Route, Switch, useLocation } from 'react-router-dom';
import type { Location } from 'history';
import {
    LoginPage,
    PasswordResetCodePage,
    RequestPasswordResetPage,
    VerifyEmailPage,
    InviteRegisterPage,
    CompleteRegistrationPage,
} from 'c-auth-module/Pages';
import { InviteRoutes, PreAuthRoutes } from 'c-routes';
import { useSelector } from 'react-redux';
import { authSelectors } from 'c-auth-module/Slices';
import { usePostAuthHome } from 'c-wrapper/Components/CommonWrapper';
import Revalidate from 'c-auth-module/Components/Revalidate/Revalidate';

const preAuthPaths: string[] = Object.values(PreAuthRoutes);

const PreAuth = () => {
    const $user = useSelector(authSelectors.authenticatedUser);
    const postAuthHome = usePostAuthHome();
    // PrivateRoute and NoUserRedirect put the page a logged-out visitor wanted here. This redirect
    // renders the moment login succeeds and unmounts the login form before its own effect can
    // run, so it is the one that has to honour it.
    const intended = useLocation<{ from?: Location } | undefined>().state?.from;
    const returnTo = intended && !preAuthPaths.includes(intended.pathname) ? intended : null;

    const redirectInviteLink = useMemo(() => {
        const token = new URLSearchParams(window.location.search).get('token');
        return $user != null && token != null && token?.length > 0
            ? generatePath(InviteRoutes.InviteToken, { token })
            : null;
    }, [$user]);

    return (
        <Switch>
            {$user && <Redirect to={redirectInviteLink ?? returnTo ?? postAuthHome} />}
            <Route path={PreAuthRoutes.Login}>
                <LoginPage />
            </Route>
            <Route path={PreAuthRoutes.Welcome}>
                <LoginPage welcomePage />
            </Route>
            <Route path={PreAuthRoutes.Revalidate}>
                <Revalidate />
            </Route>
            <Route path={PreAuthRoutes.RequestPasswordReset}>
                <RequestPasswordResetPage />
            </Route>
            <Route path={PreAuthRoutes.RequestPasswordResetCode}>
                <PasswordResetCodePage />
            </Route>
            <Route path={PreAuthRoutes.Register}>
                <InviteRegisterPage />
            </Route>
            <Route path={PreAuthRoutes.Verify}>
                <VerifyEmailPage />
            </Route>
            <Route path={PreAuthRoutes.CompleteRegistration}>
                <CompleteRegistrationPage />
            </Route>
            <Route>
                <Redirect to={PreAuthRoutes.Login} />
            </Route>
        </Switch>
    );
};

export default PreAuth;
